import useMediaQuery from "@mui/material/useMediaQuery";
import { BlockWrapper } from "@smartfire/js-block-engine";
import apiFetch from "@wordpress/api-fetch";
import clsx from "clsx";
import he from "he";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Pagination, Post, SubMenu } from "../components";
import HomeHero from "../home-hero/block";
import Filter from "./filter/Filter";
import style from "./block.module.scss";

function Block({
  cat,
  posts,
  homeHeroPosts = [],
  currentPagination = 1,
  initialFilters = [],
  initialTotalPosts,
}) {
  const title = cat?.title;
  /**
   * 1. Taxonomy of level 1: we take all level 2
   * 2. Taxonomy of level 2: we take all level 2 of the same level 1
   */
  const topCategories = cat.parent ? cat.parentChildren : cat.children;
  const hasLevel3 = cat.parent !== null && cat.children.length > 0;
  /**
   * Hook on resize
   */
  const isDesktop = useMediaQuery("(min-width:" + style.tabletBreakpoint + ")");
  const isMobile = useMediaQuery("(max-width:" + style.mobileBreakpoint + ")");
  /**
   * States
   */
  const [currentPosts, setCurrentPosts] = useState(posts);
  const [currentPage, setCurrentPage] = useState(currentPagination);
  const [listFilter, setListFilter] = useState(initialFilters);
  const [totalPosts, setTotalPosts] = useState(initialTotalPosts);
  let numberOfPost = 12;
  if (isMobile && hasLevel3) {
    numberOfPost = 9;
  } else if (isMobile) {
    numberOfPost = 5;
  }
  const numberOfPages = Math.ceil(totalPosts / numberOfPost);
  const isMounted = useRef(false);
  // React i18next
  const { t } = useTranslation();

  useEffect(() => {
    if (isMounted.current) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set("page", currentPage.toString());
      if (listFilter.length === 0) {
        urlParams.delete("filter");
      } else {
        const currentFilter = urlParams.get("filter");
        urlParams.set("filter", listFilter.join(","));
        // Detect if we changed the filter or just changing page
        if (currentFilter !== listFilter.join(",")) {
          // If we pick a new filter, we go to the first page of it.
          setCurrentPage(1);
        }
      }
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}?${urlParams}`
      );

      let postParams = {
        filter: listFilter,
        currentCategory: cat.id,
        page: currentPage,
        numberOfPost: numberOfPost,
      };
      // Fetch posts
      apiFetch({
        path: "/posts/cat?" + new URLSearchParams(postParams).toString(),
      }).then((output) => {
        // Transition from mobile to desktop might search for page that doesn't exist.
        // (i.e: 20 posts: 2 pages on Desktop, 3 on Mobile so ?page=3 doesn't exist for desktop)
        // Return to page 1 if it happens.
        if (output.allPosts) {
          setCurrentPosts(output.allPosts);
          setTotalPosts(output.total_post);
        } else {
          setCurrentPage(1);
        }
      });
    } else {
      isMounted.current = true;
    }
  }, [currentPage, listFilter, cat.id, numberOfPost]);

  /**
   * Scroll to the top of the listing after currentPage changed
   */
  useEffect(() => {
    if (isMounted) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentPage]);

  return (
    <BlockWrapper.Div className={style.block}>
      <div className={style.header}>
        <h1 className={style.title}>{he.decode(cat?.title)}</h1>

        <SubMenu arrowLeftBgColor={style.lightBeige}>
          <>
            <a
              href={cat.parent ? cat.parentLink : cat.link}
              className={cat.parent ? undefined : style.selected}
            >
              {t("All")}
            </a>

            {topCategories.map((cat, _) => (
              <a
                key={cat.link}
                href={cat.link}
                className={clsx(title === cat.name && style.selected)}
              >
                {he.decode(cat.name)}
              </a>
            ))}
          </>
        </SubMenu>
      </div>

      <HomeHero
        categoryId={cat.id}
        addClass={style.homeHero}
        posts={homeHeroPosts}
      />

      {hasLevel3 && (
        <Filter
          cat={cat}
          isDesktop={isDesktop}
          listFilter={listFilter}
          setListFilter={setListFilter}
        />
      )}

      <div className={style.grid}>
        {currentPosts &&
          currentPosts.map((post, index) => (
            <Post
              key={index}
              post={post}
              isZoom={true}
              verboseMobile={hasLevel3}
            />
          ))}
      </div>

      {numberOfPages > 1 && (
        <Pagination
          currentPagination={currentPage}
          numberOfPages={numberOfPages}
          setCurrentPage={setCurrentPage}
        />
      )}
    </BlockWrapper.Div>
  );
}

export default Block;
